import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, FolderOpen, Loader2 } from 'lucide-react'
import {
  GetGoogleDrivePickerSessionDocument,
  GetGoogleDrivePickerSessionQuery,
  GetGoogleDrivePickerSessionQueryVariables,
  GetOAuthUrlDocument,
  GetOAuthUrlQuery,
  GetOAuthUrlQueryVariables,
  GoogleDrivePickerStatus,
  ToolAuthType,
} from '__generated__/api-types-and-hooks'
import { fetcher } from 'lib/api-client'
import { logSentryError } from 'utils/helper'
import {
  CoachChatContext,
  isCoachSessionId,
} from 'components/Common/AppCoachReportBlock/coachChatContext'

/** Shown when a Google Drive tool needs the asker to connect (or connect again) Google Drive. */
export type ConnectGoogleDriveBlock = {
  type: 'connect_google_drive'
  reason: 'not_connected' | 'reconnect'
}

/**
 * A "Choose files from Google Drive" button (Google Picker). `file_ids`: files the person linked
 * to, shown ready to pick. The files picked are sent to Coach in a message with their links.
 */
export type GoogleDrivePickerBlock = {
  type: 'google_drive_picker'
  file_ids?: string[]
}

/** A file create_google_file made in the asker's own Google Drive. */
export type GoogleDriveFileBlock = {
  type: 'google_drive_file'
  kind: 'document' | 'spreadsheet' | 'presentation'
  title: string
  file_id: string
  url: string
}

// The connector name of the Google Drive tool (Tools table), sent to getOAuthUrl
const GOOGLE_DRIVE_CONNECTOR = 'Google Drive'

// How long "Opening Google…" stays up after the browser was sent to Google. A page still here by
// then did not leave (e.g. the person chose to stay on a page with unsaved changes, or pressed
// Esc), so the button works again.
const LEAVE_WAIT_MS = 5000

// Google's script loader, which loads the Picker. Google serves it only from here (no npm package).
export const GOOGLE_API_SCRIPT = 'https://apis.google.com/js/api.js'

// How long Google's script, and then the Picker it loads, may each take before the card says it
// failed
const PICKER_LOAD_TIMEOUT_MS = 15000

// A Google Drive file id
const FILE_ID = /^[A-Za-z0-9_-]{10,200}$/

const MAX_FILE_NAME_LENGTH = 200

// ---------------------------------------------------------------------------
// Link checks — saved messages, stream lines, API answers and the Picker's answer
// are not trusted, so every link is checked before it is offered, followed or sent.
// ---------------------------------------------------------------------------

const httpsUrl = (href: unknown): URL | null => {
  if (typeof href !== 'string') return null
  let url: URL
  try {
    url = new URL(href)
  } catch {
    return null
  }
  return url.protocol === 'https:' && !url.username && !url.password ? url : null
}

/** Google's sign-in page, as getOAuthUrl answers: the only place Connect Google Drive goes to. */
export const googleSignInUrl = (href: unknown): string | null => {
  const url = httpsUrl(href)
  return url && url.hostname === 'accounts.google.com' ? url.href : null
}

/** A link that opens a Google Docs, Sheets or Slides file (docs.google.com), or null. */
export const googleFileUrl = (href: unknown): string | null => {
  const url = httpsUrl(href)
  return url && url.hostname === 'docs.google.com' ? url.href : null
}

/** A picked file's link: Google Docs, Sheets or Slides, or another file in Google Drive. */
const pickedFileUrl = (href: unknown): URL | null => {
  const url = httpsUrl(href)
  return url && (url.hostname === 'docs.google.com' || url.hostname === 'drive.google.com')
    ? url
    : null
}

/**
 * The link Coach gets for a picked file. Coach finds the file by the id in it (/d/<id> or
 * ?id=<id>), so the Picker's link is kept only when it carries that file's id. Any other link
 * (e.g. Apps Script, Sites or My Maps, which open on other Google hosts) becomes a Drive link made
 * from the checked id: Coach can then find the file and say plainly if it can't read it.
 */
const pickedFileLink = (id: string, href: unknown): string => {
  const url = pickedFileUrl(href)
  const linkedId =
    url && (/\/d\/([A-Za-z0-9_-]{10,200})/.exec(url.pathname)?.[1] ?? url.searchParams.get('id'))
  return url && linkedId === id ? url.href : `https://drive.google.com/file/d/${id}/view`
}

/**
 * Where Google sends the person back to: this page, with a flash the floating chat reads to open
 * this Coach thread again. Never `connectorName` (see canvaReturnUrl).
 */
export const googleDriveReturnUrl = (pageUrl: string, sessionId?: string | null): string => {
  const url = new URL(pageUrl)
  const flash = 'coachReturn=googleDrive'
  url.searchParams.set(
    'flash',
    isCoachSessionId(sessionId) ? `${flash}&coachSession=${sessionId}` : flash
  )
  return url.toString()
}

// ---------------------------------------------------------------------------
// Google Picker — Google's script is added to the page on first use
// ---------------------------------------------------------------------------

/** The parts of Google's Picker API (google.picker) the card uses. */
interface PickerDocsView {
  setIncludeFolders(include: boolean): PickerDocsView
  setSelectFolderEnabled(enabled: boolean): PickerDocsView
  setFileIds(fileIds: string): PickerDocsView
  setMode(mode: string): PickerDocsView
}

type PickerResponse = Record<string, unknown>

type PickerInstance = { setVisible(visible: boolean): void; dispose(): void }

interface PickerBuilder {
  addView(view: PickerDocsView): PickerBuilder
  enableFeature(feature: string): PickerBuilder
  setOAuthToken(token: string): PickerBuilder
  setDeveloperKey(key: string): PickerBuilder
  setAppId(appId: string): PickerBuilder
  setOrigin(origin: string): PickerBuilder
  setLocale(locale: string): PickerBuilder
  setCallback(callback: (data: PickerResponse) => void): PickerBuilder
  build(): PickerInstance
}

interface GooglePicker {
  DocsView: new (viewId: string) => PickerDocsView
  DocsViewMode: { LIST: string }
  PickerBuilder: new () => PickerBuilder
  ViewId: { DOCS: string }
  Feature: { MULTISELECT_ENABLED: string }
  Action: { PICKED: string; ERROR: string }
  Response: { ACTION: string; DOCUMENTS: string }
  Document: { ID: string; NAME: string; URL: string }
}

interface GoogleApiLoader {
  load(
    library: string,
    options: { callback: () => void; onerror: () => void; timeout: number; ontimeout: () => void }
  ): void
}

/** The page's window, with what Google's script adds to it */
const googleWindow = () =>
  window as Window & { gapi?: GoogleApiLoader; google?: { picker?: GooglePicker } }

/** Google's script loader: added to the page, or the one already there. */
const loadGoogleApi = (): Promise<GoogleApiLoader> =>
  new Promise((resolve, reject) => {
    const loaded = googleWindow().gapi
    if (loaded?.load) {
      resolve(loaded)
      return
    }
    // A script still on its way from an earlier try is waited for again, never added twice:
    // taking it out would not stop it, and Google's script would then run twice
    const pending = Array.from(document.head.querySelectorAll('script')).find(
      (tag) => tag.src === GOOGLE_API_SCRIPT
    )
    const script = pending ?? document.createElement('script')
    const timer = window.setTimeout(
      () => reject(new Error('Google script took too long to load')),
      PICKER_LOAD_TIMEOUT_MS
    )
    // A script that failed is taken out again: the next try adds a new one
    script.addEventListener(
      'load',
      () => {
        window.clearTimeout(timer)
        const gapi = googleWindow().gapi
        if (gapi?.load) {
          resolve(gapi)
        } else {
          script.remove()
          reject(new Error('Google script did not start'))
        }
      },
      { once: true }
    )
    script.addEventListener(
      'error',
      () => {
        window.clearTimeout(timer)
        script.remove()
        reject(new Error('Google script did not load'))
      },
      { once: true }
    )
    if (!pending) {
      script.src = GOOGLE_API_SCRIPT
      script.async = true
      document.head.appendChild(script)
    }
  })

let pickerLoading: Promise<GooglePicker> | null = null

/** Google's Picker, loaded once: Google's script, then its picker library. */
const loadGooglePicker = (): Promise<GooglePicker> => {
  if (!pickerLoading) {
    const loading = loadGoogleApi().then(
      (gapi) =>
        new Promise<GooglePicker>((resolve, reject) => {
          gapi.load('picker', {
            callback: () => {
              const picker = googleWindow().google?.picker
              if (picker) resolve(picker)
              else reject(new Error('Google Picker did not start'))
            },
            onerror: () => reject(new Error('Google Picker did not load')),
            timeout: PICKER_LOAD_TIMEOUT_MS,
            ontimeout: () => reject(new Error('Google Picker took too long to load')),
          })
        })
    )
    // A failed load (e.g. no connection, or a blocker) can be tried again by the next click
    loading.catch(() => {
      if (pickerLoading === loading) pickerLoading = null
    })
    pickerLoading = loading
  }
  return pickerLoading
}

// The Picker's codes for the app's languages where they differ
const PICKER_LOCALES: Record<string, string> = { tl: 'fil', zh: 'zh-CN' }

const pickerLocale = (language?: string): string => {
  const code = (language || 'en').toLowerCase().split('-')[0]
  return PICKER_LOCALES[code] ?? code
}

// Control characters, white space, and the marks that change the direction of text (they can
// make a name read as another one). Not all format characters: emoji need the zero-width joiner.
const NAME_BREAKS = /[\p{Cc}\s\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]+/gu

/** A file name on one line (the message lists one file per line), at most 200 characters. */
const fileNameOf = (name: unknown): string =>
  typeof name === 'string'
    ? Array.from(name.replace(NAME_BREAKS, ' ').trim())
        .slice(0, MAX_FILE_NAME_LENGTH)
        .join('')
        .trim()
    : ''

/**
 * Text the person's chat bubble shows as it is: the bubble reads Markdown and HTML, so every ASCII
 * punctuation mark is escaped (Markdown allows a backslash before any of them).
 */
const markdownText = (text: string): string => text.replace(/[!-/:-@[-`{-~]/g, '\\$&')

type PickedFile = { name: string; url: string }

/**
 * The files picked, from the Picker's answer: files with a real file id only. A file the Picker
 * could not open for the app in Google Drive (driveSuccess false) was not shared with it, so only
 * its error code is kept.
 */
const pickedFilesOf = (
  picker: GooglePicker,
  data: PickerResponse,
  untitled: string
): { files: PickedFile[]; failedErrors: string[] } => {
  const files: PickedFile[] = []
  const failedErrors: string[] = []
  const docs = data[picker.Response.DOCUMENTS] ?? data.docs
  if (!Array.isArray(docs)) return { files, failedErrors }
  docs.forEach((doc) => {
    if (!doc || typeof doc !== 'object') return
    const id = doc[picker.Document.ID] ?? doc.id
    if (typeof id !== 'string' || !FILE_ID.test(id)) return
    if (doc.driveSuccess === false) {
      // A code only (never a name or an id)
      const code = String(doc.driveError ?? '')
      failedErrors.push(/^[\w.-]{1,64}$/.test(code) ? code : 'unknown')
      return
    }
    files.push({
      name: fileNameOf(doc[picker.Document.NAME] ?? doc.name) || untitled,
      url: pickedFileLink(id, doc[picker.Document.URL] ?? doc.url),
    })
  })
  return { files, failedErrors }
}

/** Whether the focus fell back to the page (what had it went away), so it can be moved on. */
const focusIsLost = () => !document.activeElement || document.activeElement === document.body

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

const focusRing = 'focus:outline-none focus-visible:appShadow-focus'
// index.css gives every element its own text color, so icons must follow their link or button
const inheritColor = '[color:inherit]'
const primaryAction = `inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-app-primary-125 px-3 text-sm font-medium text-white no-underline transition-colors hover:bg-app-primary-120 ${focusRing}`

export const AppCoachConnectGoogleDriveBlock: React.FC<{
  block: ConnectGoogleDriveBlock
  /** Takes the focus when shown: in place of the button that had it (see the picker card) */
  focusOnMount?: boolean
}> = ({ block, focusOnMount }) => {
  const { t } = useTranslation()
  const { sessionId } = useContext(CoachChatContext)
  const [status, setStatus] = useState<'idle' | 'connecting' | 'failed'>('idle')
  // The sign-in page of the last click. Trying again reuses it: asking for a new one ends this
  // sign-in on the server, while the browser may still be on its way to Google with it.
  const signInRef = useRef<string | null>(null)
  const leaveTimerRef = useRef<number>()
  const titleRef = useRef<HTMLHeadingElement>(null)

  // Read out first, then the next Tab reaches Connect
  useEffect(() => {
    if (focusOnMount && focusIsLost()) titleRef.current?.focus()
  }, [focusOnMount])

  useEffect(() => {
    // Back from Google with the browser's Back button, the page can come back exactly as it was
    // left. That sign-in may be used up by then, so the next click asks for a new one.
    const reset = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      window.clearTimeout(leaveTimerRef.current)
      signInRef.current = null
      setStatus('idle')
    }
    window.addEventListener('pageshow', reset)
    return () => {
      window.removeEventListener('pageshow', reset)
      window.clearTimeout(leaveTimerRef.current)
    }
  }, [])

  // A full page load: Google sends the person back here, and the chat opens this thread again
  const leaveFor = (signInUrl: string) => {
    window.location.assign(signInUrl)
    window.clearTimeout(leaveTimerRef.current)
    leaveTimerRef.current = window.setTimeout(() => setStatus('idle'), LEAVE_WAIT_MS)
  }

  const connect = async () => {
    // aria-disabled, not disabled, while it works: a disabled button would drop the focus
    if (status === 'connecting') return
    setStatus('connecting')
    if (signInRef.current) {
      leaveFor(signInRef.current)
      return
    }
    try {
      const data = await fetcher<GetOAuthUrlQuery, GetOAuthUrlQueryVariables>(GetOAuthUrlDocument, {
        input: {
          connectorName: GOOGLE_DRIVE_CONNECTOR,
          targetOrigin: googleDriveReturnUrl(window.location.href, sessionId),
          authType: ToolAuthType.Oauth,
        },
      })()
      const signInUrl = data?.getOAuthUrl?.success ? googleSignInUrl(data.getOAuthUrl.data) : null
      if (!signInUrl) throw new Error('No Google sign-in page')
      signInRef.current = signInUrl
      leaveFor(signInUrl)
    } catch {
      setStatus('failed')
    }
  }

  const connecting = status === 'connecting'
  return (
    <div className="[&_svg_*]:[color:inherit]">
      <h4
        ref={titleRef}
        tabIndex={-1}
        className="text-sm font-semibold text-app-grey-100 focus:outline-none"
      >
        {t('askCL.googleDrive.connectTitle')}
      </h4>
      <p className="mt-1 text-sm text-app-grey-75">
        {block.reason === 'reconnect'
          ? t('askCL.googleDrive.reconnectBody')
          : t('askCL.googleDrive.notConnectedBody')}
      </p>
      <button
        type="button"
        onClick={connect}
        aria-disabled={connecting}
        className={`${primaryAction} mt-3 aria-disabled:cursor-wait aria-disabled:opacity-70`}
      >
        {connecting && (
          <Loader2
            aria-hidden="true"
            className={`h-4 w-4 flex-none animate-spin ${inheritColor}`}
          />
        )}
        {connecting ? t('askCL.googleDrive.connecting') : t('askCL.googleDrive.connect')}
      </button>
      {status === 'failed' && (
        <p role="alert" className="mt-2 text-xs text-app-red-80">
          {t('askCL.googleDrive.connectFailed')}
        </p>
      )}
      <p className="mt-2 text-xs text-app-grey-65">{t('askCL.googleDrive.afterConnect')}</p>
    </div>
  )
}

type PickerStep =
  | { step: 'idle' | 'opening' | 'failed' | 'notConfigured' }
  | { step: 'connect'; reason: ConnectGoogleDriveBlock['reason'] }

export const AppCoachGoogleDrivePickerBlock: React.FC<{ block: GoogleDrivePickerBlock }> = ({
  block,
}) => {
  const { t, i18n } = useTranslation()
  const { sendMessage } = useContext(CoachChatContext)
  const [state, setState] = useState<PickerStep>({ step: 'idle' })
  // The files picked last, and whether the chat sent them to Coach
  const [shared, setShared] = useState<{ names: string[]; sent: boolean } | null>(null)
  // Some files of the last pick could not be shared
  const [shareFailed, setShareFailed] = useState(false)
  // The Picker shown last: closed for good when another one opens or the card goes away
  const pickerRef = useRef<PickerInstance | null>(null)
  // The session and the Picker answer after a while: the card may be gone by then
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
      pickerRef.current?.dispose()
      pickerRef.current = null
    },
    []
  )
  // Shown in place of the button: the focus the button had goes to it, not to the page
  const notConfiguredRef = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (state.step === 'notConfigured' && focusIsLost()) notConfiguredRef.current?.focus()
  }, [state.step])

  // Only a pick sends anything (the Picker also answers when it opens and when it is closed)
  const onPicked = (picker: GooglePicker) => (data: PickerResponse) => {
    if (!mountedRef.current) return
    const action = data[picker.Response.ACTION] ?? data.action
    if (action === picker.Action.ERROR) {
      setState({ step: 'failed' })
      return
    }
    if (action !== picker.Action.PICKED) return
    // It did open: an earlier "couldn't open" no longer holds
    setState((current) => (current.step === 'failed' ? { step: 'idle' } : current))
    const { files, failedErrors } = pickedFilesOf(picker, data, t('askCL.googleDrive.untitled'))
    setShareFailed(failedErrors.length > 0)
    if (failedErrors.length) {
      // Error codes only: never the token, the file ids or the names
      logSentryError('Google Picker did not grant picked files to the app', {
        driveErrors: failedErrors,
        count: failedErrors.length,
      })
    }
    if (!files.length) return
    // One file per line, as a link that shows the name word for word
    const list = files.map((file) => `[${markdownText(file.name)}](${file.url})`).join('\n')
    const sent = sendMessage?.(t('askCL.googleDrive.pickedMessage', { files: list })) === true
    setShared({ names: files.map((file) => file.name), sent })
  }

  // The access token only goes to Google's Picker: never kept, logged or shown
  const showPicker = (
    picker: GooglePicker,
    session: { accessToken: string; developerKey: string; appId: string }
  ) => {
    const fileIds = (Array.isArray(block.file_ids) ? block.file_ids : []).filter(
      (id) => typeof id === 'string' && FILE_ID.test(id)
    )
    // A list: the app sees only the files chosen, so Google advises against thumbnails
    const docsView = () =>
      new picker.DocsView(picker.ViewId.DOCS)
        .setMode(picker.DocsViewMode.LIST)
        .setIncludeFolders(false)
        .setSelectFolderEnabled(false)
    const builder = new picker.PickerBuilder()
    // The files the coach linked to come first. The whole Drive is there too: Google leaves out
    // of that view a file the person can't open (e.g. one in another account), so it may be empty.
    if (fileIds.length) builder.addView(docsView().setFileIds(fileIds.join(',')))
    pickerRef.current?.dispose()
    const instance = builder
      .addView(docsView())
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(session.accessToken)
      .setDeveloperKey(session.developerKey)
      .setAppId(session.appId)
      .setOrigin(`${window.location.protocol}//${window.location.host}`)
      .setLocale(pickerLocale(i18n.language))
      .setCallback(onPicked(picker))
      .build()
    pickerRef.current = instance
    instance.setVisible(true)
  }

  const open = async () => {
    // aria-disabled, not disabled, while it works: a disabled button would drop the focus
    if (state.step === 'opening') return
    setState({ step: 'opening' })
    setShareFailed(false)
    let next: PickerStep = { step: 'failed' }
    try {
      // Asked on every click: the token is short-lived, and an old saved card must work too
      const data = await fetcher<
        GetGoogleDrivePickerSessionQuery,
        GetGoogleDrivePickerSessionQueryVariables
      >(GetGoogleDrivePickerSessionDocument)()
      const session = data?.getGoogleDrivePickerSession
      if (session?.status === GoogleDrivePickerStatus.NotConnected) {
        next = { step: 'connect', reason: 'not_connected' }
      } else if (session?.status === GoogleDrivePickerStatus.Reconnect) {
        next = { step: 'connect', reason: 'reconnect' }
      } else if (session?.status === GoogleDrivePickerStatus.NotConfigured) {
        next = { step: 'notConfigured' }
      } else if (
        session?.status === GoogleDrivePickerStatus.Ok &&
        session.accessToken &&
        session.developerKey &&
        session.appId
      ) {
        const picker = await loadGooglePicker()
        if (!mountedRef.current) return
        showPicker(picker, {
          accessToken: session.accessToken,
          developerKey: session.developerKey,
          appId: session.appId,
        })
        next = { step: 'idle' }
      }
    } catch {
      next = { step: 'failed' }
    }
    if (mountedRef.current) setState(next)
  }

  // Both come only from a click on the button, which they replace
  if (state.step === 'connect') {
    return (
      <AppCoachConnectGoogleDriveBlock
        block={{ type: 'connect_google_drive', reason: state.reason }}
        focusOnMount
      />
    )
  }
  if (state.step === 'notConfigured') {
    return (
      <p
        ref={notConfiguredRef}
        tabIndex={-1}
        className="text-xs text-app-grey-65 focus:outline-none"
      >
        {t('askCL.googleDrive.notConfigured')}
      </p>
    )
  }

  const opening = state.step === 'opening'
  return (
    <div className="[&_svg_*]:[color:inherit]">
      <button
        type="button"
        onClick={open}
        aria-disabled={opening}
        className={`${primaryAction} aria-disabled:cursor-wait aria-disabled:opacity-70`}
      >
        {opening ? (
          <Loader2
            aria-hidden="true"
            className={`h-4 w-4 flex-none animate-spin ${inheritColor}`}
          />
        ) : (
          <FolderOpen aria-hidden="true" className={`h-4 w-4 flex-none ${inheritColor}`} />
        )}
        {opening ? t('askCL.googleDrive.opening') : t('askCL.googleDrive.chooseFiles')}
      </button>
      {state.step === 'failed' && (
        <p role="alert" className="mt-2 text-xs text-app-red-80">
          {t('askCL.googleDrive.openFailed')}
        </p>
      )}
      {shareFailed && (
        <p role="alert" className="mt-2 text-xs text-app-red-80">
          {t('askCL.googleDrive.shareFailed')}
        </p>
      )}
      {/* Always there, so screen readers read out the files when they come into it */}
      <div role="status" className={shared ? 'mt-3' : undefined}>
        {shared && (
          <>
            <p className="text-sm font-medium text-app-grey-100">{t('askCL.googleDrive.shared')}</p>
            <ul className="mt-1 list-disc ps-5 text-sm text-app-grey-75">
              {shared.names.map((name, index) => (
                <li key={index} dir="auto" className="break-words">
                  {name}
                </li>
              ))}
            </ul>
            {!shared.sent && (
              <p className="mt-1 text-xs text-app-grey-65">{t('askCL.googleDrive.askAboutThem')}</p>
            )}
          </>
        )}
      </div>
      <p className="mt-2 text-xs text-app-grey-65">{t('askCL.googleDrive.pickerNote')}</p>
    </div>
  )
}

// The kind of file, named after the Google app it opens in
const KIND_LABELS = new Map<unknown, string>([
  ['document', 'askCL.googleDrive.kindDocument'],
  ['spreadsheet', 'askCL.googleDrive.kindSpreadsheet'],
  ['presentation', 'askCL.googleDrive.kindPresentation'],
])

export const AppCoachGoogleDriveFileBlock: React.FC<{ block: GoogleDriveFileBlock }> = ({
  block,
}) => {
  const { t } = useTranslation()
  const url = googleFileUrl(block.url)
  const kindLabel = KIND_LABELS.get(block.kind)
  const title = typeof block.title === 'string' ? block.title.trim() : ''

  return (
    <div className="[&_svg_*]:[color:inherit]">
      {kindLabel && <p className="text-xs font-medium text-app-grey-65">{t(kindLabel)}</p>}
      <h4 dir="auto" className="mt-0.5 break-words text-sm font-semibold text-app-grey-100">
        {title || t('askCL.googleDrive.untitled')}
      </h4>
      {url ? (
        <>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${primaryAction} mt-3`}
          >
            <ExternalLink aria-hidden="true" className={`h-4 w-4 flex-none ${inheritColor}`} />
            {t('askCL.googleDrive.open')}
          </a>
          <p className="mt-2 text-xs text-app-grey-65">{t('askCL.googleDrive.privateNote')}</p>
        </>
      ) : (
        <p className="mt-2 text-xs text-app-grey-65">{t('askCL.googleDrive.unavailable')}</p>
      )}
    </div>
  )
}
